import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GameController, Plus, Link, BookOpen, GraduationCap, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublisherGames() {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true })
      .then(r => setGames(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [publisherId]);

  const loadAllGames = async () => {
    if (allGames.length) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/games`, { withCredentials: true });
      setAllGames(data || []);
    } catch {}
  };

  const linkGame = async () => {
    if (!selectedSlug) return;
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/games/link`,
        { game_slug: selectedSlug }, { withCredentials: true });
      toast.success('Game linked!');
      setShowLink(false);
      setSelectedSlug('');
      const { data } = await axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true });
      setGames(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const base = `/publisher/${publisherId}`;
  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#F59E0B]/50 focus:outline-none";

  return (
    <div data-testid="publisher-games-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Games</h1>
          <p className="text-sm text-[#666] mt-1">{games.length} games in your catalog</p>
        </div>
        <button onClick={() => { setShowLink(!showLink); loadAllGames(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm hover:bg-[#D97706] transition-colors"
          data-testid="link-game-btn">
          <Link size={16} weight="bold" /> Link Game
        </button>
      </div>

      {showLink && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 mb-6">
          <p className="text-sm text-white font-semibold mb-3">Link a game to your publisher</p>
          <div className="flex gap-3">
            <select className={inputCls} value={selectedSlug} onChange={e => setSelectedSlug(e.target.value)}
              data-testid="link-game-select">
              <option value="">Select a game...</option>
              {allGames.map(g => (
                <option key={g.slug} value={g.slug}>{g.name}</option>
              ))}
            </select>
            <button onClick={linkGame} className="px-6 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm whitespace-nowrap"
              data-testid="link-game-submit">Link</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-28 bg-white/5 rounded-xl animate-pulse" />)
        ) : games.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-10 text-center">
            <GameController size={44} className="mx-auto text-[#333] mb-4" />
            <p className="text-white font-medium text-lg mb-1">No Games Yet</p>
            <p className="text-sm text-[#555]">Link your published games to manage content.</p>
          </div>
        ) : games.map((g, i) => (
          <div key={g.slug || i}
            className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 hover:bg-white/[0.04] transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                <GameController size={28} weight="duotone" className="text-[#F59E0B]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white font-bold text-lg">{g.name}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#777] capitalize">{g.relationship_type}</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {(g.categories || []).slice(0, 5).map(cat => (
                    <span key={cat} className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 text-[#888]">{cat}</span>
                  ))}
                </div>
                <div className="flex gap-4 text-[11px] text-[#666]">
                  <span>{g.min_players}-{g.max_players} players</span>
                  {g.play_time && <span>{g.play_time}</span>}
                  {g.complexity && <span>Complexity: {g.complexity}</span>}
                </div>
                {/* Content counts */}
                <div className="flex gap-4 mt-3 pt-3 border-t border-white/[0.04]">
                  <button onClick={() => navigate(`${base}/rules?game=${g.id}`)}
                    className="flex items-center gap-1.5 text-xs text-[#00F0FF] hover:text-[#00D4E0] transition-colors">
                    <BookOpen size={14} /> {g.rulesets_count || 0} Rulesets
                  </button>
                  <button onClick={() => navigate(`${base}/tutorials?game=${g.id}`)}
                    className="flex items-center gap-1.5 text-xs text-[#10B981] hover:text-[#059669] transition-colors">
                    <GraduationCap size={14} /> {g.tutorials_count || 0} Tutorials
                  </button>
                  <span className="flex items-center gap-1.5 text-xs text-[#9D4CDD]">
                    <Package size={14} /> {g.editions_count || 0} Editions · {g.expansions_count || 0} Expansions
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
